import React, { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import SafeAreaLayout from '@/components/layout/SafeAreaLayout'
import { useGetTransactions, TransactionTypes } from '@/domain'
import { useTheme } from '@/hooks/useTheme'
import { TransactionsList } from '@/components/TransactionsList'
import { TransactionSkeleton } from '@/components/LoadingSkeletons/TransactionSkeleton'

// Card transaction types
const CARD_TRANSACTION_TYPES: TransactionTypes[] = [
  'card_write_off',
  'card_top_up',
  'cashback_payout',
]

export const KemCardActivitiesScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()

  // Fetch transactions filtered by card transaction types
  const {
    data: transactionsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetTransactions({
    types: CARD_TRANSACTION_TYPES,
    page_size: 20, // Standard page size for pagination
  })

  // Get all transactions and filter for card transactions
  const cardTransactions = useMemo(() => {
    if (!transactionsData?.pages) return []

    const allTransactions = transactionsData.pages.flatMap(
      (page) => page.transactions,
    )

    // Filter for card transactions only
    return allTransactions.filter((transaction) =>
      CARD_TRANSACTION_TYPES.includes(transaction.type as any),
    )
  }, [transactionsData?.pages])

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleGoBack = () => {
    navigate(-1)
  }

  return (
    <SafeAreaLayout>
      <BaseView className="h-full">
        {/* Header */}
        <BaseView
          px={12}
          py={16}
          row
          align="center"
          gap={12}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: theme.colors.background.screen,
          }}
        >
          <BaseView
            onClick={handleGoBack}
            style={{
              cursor: 'pointer',
              width: 32,
              height: 32,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 16,
            }}
          >
            <AppIcons name="chevronLeft" size={24} color="icon.default" />
          </BaseView>

          <BaseText fontSize={20} fontFamily="semibold" color="label.default">
            {t('screens:activity.title')}
          </BaseText>
        </BaseView>

        {/* Card Activities List */}
        <BaseView flex={1} px={12} style={{ paddingBottom: 80 }}>
          {isLoading ? (
            <BaseView py={24}>
              <TransactionSkeleton count={5} />
            </BaseView>
          ) : cardTransactions.length === 0 ? (
            // Custom empty state for card transactions
            <BaseView flex={1} center py={40}>
              <BaseView align="center" gap={16}>
                <BaseView
                  style={{
                    width: 80,
                    height: 80,
                    backgroundColor: theme.colors.background.secondaryGray,
                    borderRadius: 40,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AppIcons name="card" color="#9CA3AF" size={40} />
                </BaseView>

                <BaseView align="center" gap={8}>
                  <BaseText
                    fontSize={18}
                    fontFamily="semibold"
                    color="label.default"
                    textAlign="center"
                  >
                    {t(
                      'translation:transactionsNotFound',
                      'No card transactions yet',
                    )}
                  </BaseText>

                  <BaseText
                    fontSize={16}
                    fontFamily="regular"
                    color="label.description"
                    textAlign="center"
                    style={{ maxWidth: '280px' }}
                  >
                    {t(
                      'emptyTransactions',
                      'Future transactions will appear here',
                    )}
                  </BaseText>
                </BaseView>
              </BaseView>
            </BaseView>
          ) : (
            <TransactionsList
              transactions={cardTransactions}
              isLoading={false}
              onLoadMore={handleLoadMore}
              hasNextPage={hasNextPage}
              isLoadingMore={isFetchingNextPage}
            />
          )}
        </BaseView>
      </BaseView>
    </SafeAreaLayout>
  )
}
